import { postGrowthEngineAnalytics } from './growthEngineApi'
import { OLLIE_PLATFORM } from './ollieFindingsApi'

export const OLLIE_CONTEXTUAL_ASK_OPENED = 'ollie_contextual_ask_opened'
export const OLLIE_DISCOVERABILITY_EXPOSED = 'ollie_discoverability_exposed'
export const OLLIE_FIRST_USE_INTRO_DISMISSED = 'ollie_first_use_intro_dismissed'
export const OLLIE_FIRST_USE_INTRO_PROMPT_SELECTED =
  'ollie_first_use_intro_prompt_selected'

const exposedKeys = new Set()

const postOllieDiscoverability = (venueId, body) => {
  if (!venueId || !body?.eventName) return Promise.resolve(null)
  return postGrowthEngineAnalytics(venueId, {
    platform: body.platform || OLLIE_PLATFORM,
    ...body,
  }).catch(() => null)
}

const withCohort = (metadata, cohort) => ({
  ...metadata,
  ...(cohort && cohort !== 'all' ? { cohort: String(cohort) } : {}),
  source: 'ollie',
})

/** Once per session/venue/entryPoint — meaningful exposure only. */
export function trackOllieDiscoverabilityExposed({
  venueId,
  entryPoint,
  page = null,
  cohort = null,
  platform = OLLIE_PLATFORM,
} = {}) {
  if (!venueId || !entryPoint) return Promise.resolve(null)
  const key = `${venueId}:${entryPoint}:${page || ''}`
  if (exposedKeys.has(key)) return Promise.resolve(null)
  exposedKeys.add(key)
  return postOllieDiscoverability(venueId, {
    eventName: OLLIE_DISCOVERABILITY_EXPOSED,
    platform,
    metadata: withCohort(
      {
        entryPoint: String(entryPoint),
        ...(page ? { page: String(page) } : {}),
      },
      cohort,
    ),
  })
}

export function resetOllieDiscoverabilityExposureForTests() {
  exposedKeys.clear()
}

export function trackOllieFirstUseIntroDismissed({
  venueId,
  cohort = null,
  platform = OLLIE_PLATFORM,
} = {}) {
  return postOllieDiscoverability(venueId, {
    eventName: OLLIE_FIRST_USE_INTRO_DISMISSED,
    platform,
    metadata: withCohort({ surface: 'opening' }, cohort),
  })
}

export function trackOllieFirstUseIntroPromptSelected({
  venueId,
  promptId = null,
  question = null,
  cohort = null,
  platform = OLLIE_PLATFORM,
} = {}) {
  return postOllieDiscoverability(venueId, {
    eventName: OLLIE_FIRST_USE_INTRO_PROMPT_SELECTED,
    platform,
    metadata: withCohort(
      {
        surface: 'opening',
        ...(promptId ? { promptId: String(promptId) } : {}),
        ...(question ? { question: String(question).slice(0, 200) } : {}),
      },
      cohort,
    ),
  })
}

/**
 * Measurable open for contextual "Ask Ollie about this" entry points.
 * Fire-and-forget — never blocks opening the panel.
 */
export function trackOllieContextualAskOpened({
  venueId,
  entryPoint,
  page = null,
  checkId = null,
  question = null,
  cohort = null,
  platform = OLLIE_PLATFORM,
} = {}) {
  if (!venueId || !entryPoint) return Promise.resolve(null)
  return postOllieDiscoverability(venueId, {
    eventName: OLLIE_CONTEXTUAL_ASK_OPENED,
    platform,
    intentId: checkId ? `ollie:${checkId}` : undefined,
    metadata: withCohort(
      {
        entryPoint: String(entryPoint),
        ...(page ? { page: String(page) } : {}),
        ...(checkId ? { checkId: String(checkId) } : {}),
        ...(question ? { question: String(question).slice(0, 200) } : {}),
        hasFinding: Boolean(checkId),
      },
      cohort,
    ),
  })
}
